import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatDMY, todayISO } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { BANK_ACCOUNTS } from "@/lib/bankAccounts";
import { Plus, Pencil, Trash2, UserPlus, Users as UsersIcon, UserCheck, CalendarClock, ChevronDown, MapPin, Mail, Pause, Play, Video, Eye, FileText, Search, X, NotebookPen, ArrowLeftRight, PauseCircle } from "lucide-react";
import { MeetIcon, ZoomIcon } from "@/components/MeetIcons";
import ScheduleEditor, { buildScheduleLabel } from "@/components/ScheduleEditor";
import StudentAttendanceDialog from "@/components/StudentAttendanceDialog";
import ChangePlanDialog from "@/components/ChangePlanDialog";
import PauseEnrollmentDialog from "@/components/PauseEnrollmentDialog";
import SuspendBillingDialog from "@/components/SuspendBillingDialog";
import { MemberActionsHelp } from "@/components/MemberActionsHelp";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

/** Niveles del marco europeo; definen cuál plan de estudio sigue la clase. */
const LEVELS = ["A1", "A2", "B1", "B2", "C1"];

export default function Groups({ mode = "all" }) {
  // mode: "all" | "group" | "private"
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [openGroup, setOpenGroup] = useState(false);
  const [openEnroll, setOpenEnroll] = useState(false);
  const [openPrivate, setOpenPrivate] = useState(false);
  const [enrollFor, setEnrollFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [attViewer, setAttViewer] = useState(null); // { student, group, enrollment, totalPerCycle }
  const [planTarget, setPlanTarget] = useState(null); // enrollment for "Cambiar plan"
  const [pauseTarget, setPauseTarget] = useState(null); // enrollment for "Pausar programa"
  const [suspendTarget, setSuspendTarget] = useState(null); // enrollment for "Suspender cobro"
  // Las sedes arrancan CERRADAS y se abren al hacer clic (pedido de la dueña,
  // 05/10/2026: con todo abierto la pantalla dibuja cientos de filas y se
  // siente trabada). Se recuerda por modo (grupal / privado / todos) cuáles
  // dejó abiertas.
  const expandKey = `expandedSedes:${mode}`;
  const [expandedSedes, setExpandedSedes] = useState(() => {
    try {
      const v = localStorage.getItem(expandKey);
      return v ? JSON.parse(v) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { localStorage.setItem(expandKey, JSON.stringify(expandedSedes)); } catch { /* ignore */ }
  }, [expandedSedes, expandKey]);
  // Enrollment-creation start date (used by Enroll dialog and Private group dialog)
  const today = todayISO();
  const [enrollStartDate, setEnrollStartDate] = useState(today);
  const [enrollStudentQuery, setEnrollStudentQuery] = useState("");
  const [enrollSelectedStudent, setEnrollSelectedStudent] = useState(null); // student object once picked
  const [enrollCustomPrice, setEnrollCustomPrice] = useState("");
  const [enrollCustomCycle, setEnrollCustomCycle] = useState("");
  const [enrollStartsOnDay, setEnrollStartsOnDay] = useState("");
  const [enrollPaidOnStart, setEnrollPaidOnStart] = useState(false);
  // Fecha de venta (en qué mes cuenta la venta) y fecha de pago (en qué mes
  // entra la plata). Vacío = venta el día de inicio, pago hoy.
  const [enrollSaleDate, setEnrollSaleDate] = useState("");
  const [enrollPaidDate, setEnrollPaidDate] = useState("");
  const [enrollSharedBilling, setEnrollSharedBilling] = useState(false);
  const [enrollPaymentLink, setEnrollPaymentLink] = useState("");
  const [enrollHistorical, setEnrollHistorical] = useState(false);
  const [enrollScholarship, setEnrollScholarship] = useState(false);
  const [enrollScholarshipCycles, setEnrollScholarshipCycles] = useState("1");
  const [enrollScholarshipNotify, setEnrollScholarshipNotify] = useState(true);
  const [enrollSelfBooking, setEnrollSelfBooking] = useState(false);
  const [enrollSendWelcome, setEnrollSendWelcome] = useState(true);
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);
  // Welcome email preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewError, setPreviewError] = useState("");
  const openWelcomePreview = async (payload) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml("");
    setPreviewError("");
    try {
      const r = await api.post("/welcome-email/preview", payload);
      setPreviewHtml(r.data?.html || "");
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || "Error");
    } finally {
      setPreviewLoading(false);
    }
  };
  // Inline editor: which enrollment is being edited and its draft values
  const [editingStartFor, setEditingStartFor] = useState(null); // enrollment_id
  const [editingStartDraft, setEditingStartDraft] = useState("");
  const [editingPaymentDraft, setEditingPaymentDraft] = useState("");
  const [editingBankDraft, setEditingBankDraft] = useState("");
  // Full "Edit Enrollment" modal (custom price, cycle, starts_on_day, paid_on_start, shared_billing)
  const [openEditEnroll, setOpenEditEnroll] = useState(false);
  const [editEnrollTarget, setEditEnrollTarget] = useState(null); // enrollment being edited
  const [editEnrollForm, setEditEnrollForm] = useState({
    custom_price: "",
    custom_cycle: "",
    starts_on_day: "",
    paid_on_start: false,
    shared_billing: false,
    flexible_schedule: false,
    self_booking: false,
    scholarship: false,
    scholarship_cycles: "1",
  });
  const [editEnrollSaving, setEditEnrollSaving] = useState(false);
  const openEditEnrollment = (e) => {
    setEditEnrollTarget(e);
    setEditEnrollForm({
      custom_price: e.amount != null ? String(e.amount) : "",
      custom_cycle: e.classes_included != null ? String(e.classes_included) : "",
      starts_on_day: e.starts_on_day != null ? String(e.starts_on_day) : "",
      paid_on_start: !!e.paid_on_start,
      shared_billing: !!e.shared_billing,
      flexible_schedule: !!e.flexible_schedule,
      self_booking: !!e.self_booking,
      scholarship: !!e.scholarship,
      scholarship_cycles: e.scholarship_cycles != null ? String(e.scholarship_cycles) : "1",
      scholarship_notify: true,
    });
    setOpenEditEnroll(true);
  };
  const submitEditEnrollment = async () => {
    if (!editEnrollTarget) return;
    try {
      setEditEnrollSaving(true);
      const payload = {
        amount: editEnrollForm.custom_price !== "" && editEnrollForm.custom_price != null
          ? Number(editEnrollForm.custom_price)
          : null,
        classes_included: editEnrollForm.custom_cycle !== "" && editEnrollForm.custom_cycle != null
          ? Number(editEnrollForm.custom_cycle)
          : null,
        starts_on_day: editEnrollForm.starts_on_day !== "" && editEnrollForm.starts_on_day != null
          ? Number(editEnrollForm.starts_on_day)
          : null,
        paid_on_start: !!editEnrollForm.paid_on_start && !editEnrollForm.scholarship,
        shared_billing: !!editEnrollForm.shared_billing,
        flexible_schedule: !!editEnrollForm.flexible_schedule,
        scholarship: !!editEnrollForm.scholarship,
        scholarship_cycles: editEnrollForm.scholarship
          ? (parseInt(editEnrollForm.scholarship_cycles, 10) || 1) : null,
        scholarship_notify: editEnrollForm.scholarship_notify !== false,
        // Self-booking only exists for private classes; open groups always get false.
        self_booking: !!editEnrollForm.self_booking
          && !!groups.find((g) => g.id === editEnrollTarget.group_id)?.is_private,
      };
      const r = await api.patch(`/enrollments/${editEnrollTarget.id}`, payload);
      const m = r.data?.shared_billing_merge;
      if (m?.merged) {
        const anuladas = (m.annulled_invoices || []).join(", ");
        toast.success(
          `Facturas unidas: queda ${m.kept_invoice || "la del compañero"}` +
          (anuladas ? ` y se anuló ${anuladas}` : ""),
          { duration: 8000 },
        );
      } else if (m && m.reason === "sibling_without_pending_invoice") {
        toast.warning("Se activó la facturación compartida, pero el compañero no tiene una factura pendiente que compartir. Las facturas ya pagadas no se tocan.", { duration: 9000 });
      } else if (m && m.reason === "no_sibling") {
        toast.warning("Se activó la facturación compartida, pero este grupo no tiene un segundo estudiante.", { duration: 8000 });
      } else {        toast.success(t("saved"));
      }
      setOpenEditEnroll(false);
      setEditEnrollTarget(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("error_generic");
      toast.error(msg);
    } finally {
      setEditEnrollSaving(false);
    }
  };
  const [form, setForm] = useState({ name: "", sede_id: "", program_id: "", level: "", teacher_id: "", is_private: false, max_students: "", schedule: "", schedule_days: [], schedule_start_time: "", schedule_end_time: "", schedule_times_by_day: null, meet_platform: "" });
  const [privForm, setPrivForm] = useState({ name: "", sede_id: "", program_id: "", level: "", teacher_id: "", schedule: "", schedule_days: [], schedule_start_time: "", schedule_end_time: "", schedule_times_by_day: null, student_ids: [], start_date: "", custom_price: "", custom_cycle: "", starts_on_day: "", paid_on_start: false, sale_date: "", paid_date: "", exclude_from_revenue: false, meet_platform: "", payment_link: "", self_booking: false, send_welcome_email: true, shared_billing: false, scholarship: false, scholarship_cycles: "1" });
  const [editingPrivate, setEditingPrivate] = useState(null); // { groupId, enrollmentId } when editing
  const [privStudentQuery, setPrivStudentQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // Filters applied to the sede-grouped list (search + teacher filter).
  // Both apply on top of each other (AND). Empty = no filter.
  const [searchQuery, setSearchQuery] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("all"); // "all" | teacher_id | "unassigned"
  const [archivedCounts, setArchivedCounts] = useState({}); // group_id -> programas archivados

  const load = async () => {
    const qs = showArchived ? "?include_archived=true" : "";
    // Todo en paralelo: antes las matrículas se pedían DESPUÉS de que
    // terminaran grupos/sedes/programas/usuarios, así que la pantalla sumaba
    // los tiempos en fila (pedido de la dueña, 05/10/2026). De `users` solo se
    // necesitan nombre, correo y rol: el resto son ~110 KB de puro lastre.
    const enrQs = showArchived ? "?with_consumption=true&only_archived=true" : "?with_consumption=true";
    const [g, s, p, allUsers, enr, arc] = await Promise.all([
      api.get(`/groups${qs}`),
      api.get("/sedes"),
      api.get("/programs"),
      api.get("/users?fields=user_id,name,email,role"),
      api.get(`/enrollments${enrQs}`),
      showArchived ? Promise.resolve({ data: [] }) : api.get("/enrollments?only_archived=true").catch(() => ({ data: [] })),
    ]);
    setGroups(g.data); setSedes(s.data); setPrograms(p.data);
    setTeachers(allUsers.data.filter((u) => u.role === "teacher"));
    setStudents(allUsers.data.filter((u) => u.role === "student"));
    setEnrollments(enr.data);
    // Para avisar en la tarjeta cuando un grupo se ve vacío porque su
    // estudiante está archivado (antes solo decía "Sin datos").
    const counts = {};
    (arc.data || []).forEach((e) => { counts[e.group_id] = (counts[e.group_id] || 0) + 1; });
    setArchivedCounts(counts);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const sedesMap = useMemo(() => Object.fromEntries(sedes.map((x) => [x.id, x.name])), [sedes]);
  const programsMap = useMemo(() => Object.fromEntries(programs.map((x) => [x.id, x.name])), [programs]);
  const usersMap = useMemo(() => {
    const all = [...teachers, ...students];
    return Object.fromEntries(all.map((u) => [u.user_id, u]));
  }, [teachers, students]);

  const groupEnrollments = (gid) =>
    enrollments
      .filter((e) => e.group_id === gid)
      .slice()
      .sort((a, b) => (usersMap[a.student_id]?.name || "")
        .localeCompare(usersMap[b.student_id]?.name || "", "es", { sensitivity: "base" }));

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", sede_id: sedes[0]?.id ?? "", program_id: programs[0]?.id ?? "", level: "", teacher_id: "", is_private: false, max_students: "", schedule: "", schedule_days: [], schedule_start_time: "", schedule_end_time: "", schedule_times_by_day: null, meet_platform: "" });
    setOpenGroup(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name, sede_id: g.sede_id, program_id: g.program_id, level: g.level || "",
      teacher_id: g.teacher_id || "", is_private: g.is_private,
      max_students: g.max_students ?? "",
      schedule: g.schedule || "",
      schedule_days: g.schedule_days || [],
      schedule_start_time: g.schedule_start_time || "",
      schedule_end_time: g.schedule_end_time || "",
      schedule_times_by_day: g.schedule_times_by_day || null,
      meet_platform: g.meet_platform || "",
    });
    setOpenGroup(true);
  };
  const openCreatePrivate = () => {
    setEditingPrivate(null);
    setPrivForm({
      name: "", sede_id: sedes[0]?.id ?? "", program_id: programs[0]?.id ?? "", level: "",
      teacher_id: "", schedule: "", schedule_days: [], schedule_start_time: "", schedule_end_time: "", schedule_times_by_day: null,
      student_ids: [], start_date: today, custom_price: "", custom_cycle: "", meet_platform: "", payment_link: "", paid_on_start: false, sale_date: "", paid_date: today, exclude_from_revenue: false, shared_billing: false, scholarship: false, scholarship_cycles: "1",
    });
    setOpenPrivate(true);
  };

  const openEditPrivate = async (g) => {
    // Se incluyen los programas ARCHIVADOS: si el único estudiante está
    // archivado, el grupo se ve vacío y el formulario no tenía a quién aplicar
    // el precio ni las sesiones por ciclo (el cambio se perdía).
    let groupEnrs = enrollments.filter((e) => e.group_id === g.id);
    try {
      const r = await api.get("/enrollments", {
        params: { group_id: g.id, include_archived: true },
      });
      if (r.data?.length) groupEnrs = r.data;
    } catch { /* se usa lo que ya está cargado */ }
    const firstEnr = groupEnrs[0];
    setEditingPrivate({
      groupId: g.id,
      enrollmentIds: groupEnrs.map((e) => e.id),
    });
    setPrivForm({
      name: g.name || "",
      sede_id: g.sede_id || "",
      program_id: g.program_id || "",
      level: g.level || "",
      teacher_id: g.teacher_id || "",
      schedule: g.schedule || "",
      schedule_days: g.schedule_days || [],
      schedule_start_time: g.schedule_start_time || "",
      schedule_end_time: g.schedule_end_time || "",
      schedule_times_by_day: g.schedule_times_by_day || null,
      meet_platform: g.meet_platform || "",
      // Students are already enrolled — we don't re-pick them in edit mode
      student_ids: groupEnrs.map((e) => e.student_id),
      start_date: firstEnr?.start_date || today,
      custom_price: firstEnr?.amount != null ? String(firstEnr.amount) : "",
      custom_cycle: firstEnr?.classes_included != null ? String(firstEnr.classes_included) : "",
      starts_on_day: firstEnr?.starts_on_day != null ? String(firstEnr.starts_on_day) : "",
      paid_on_start: !!firstEnr?.paid_on_start,
      exclude_from_revenue: false,
      payment_link: firstEnr?.payment_link || "",
      self_booking: !!firstEnr?.self_booking,
      scholarship: !!firstEnr?.scholarship,
      scholarship_cycles: firstEnr?.scholarship_cycles != null
        ? String(firstEnr.scholarship_cycles) : "1",
    });
    setOpenPrivate(true);
  };

  const submit = async () => {
    if (!form.name || !form.sede_id || !form.program_id) { toast.error(t("error_generic")); return; }
    try {
      const autoLabel = buildScheduleLabel(form.schedule_days, form.schedule_start_time, form.schedule_end_time, t, form.schedule_times_by_day);
      const payload = {
        ...form,
        teacher_id: form.teacher_id || null,
        level: form.level || null,
        max_students: form.max_students === "" || form.max_students === null
          ? null : Number(form.max_students),
        schedule_start_time: form.schedule_start_time || null,
        schedule_end_time: form.schedule_end_time || null,
        schedule_times_by_day: form.schedule_times_by_day || null,
        schedule: autoLabel || form.schedule || "",
        meet_platform: form.meet_platform || null,
      };
      if (editing) await api.put(`/groups/${editing.id}`, payload);
      else await api.post("/groups", payload);
      toast.success(t("saved"));
      setOpenGroup(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const submitPrivate = async () => {
    if (!privForm.name || !privForm.sede_id || !privForm.program_id || privForm.student_ids.length === 0) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      const autoLabel = buildScheduleLabel(privForm.schedule_days, privForm.schedule_start_time, privForm.schedule_end_time, t, privForm.schedule_times_by_day);
      const payload = {
        name: privForm.name,
        sede_id: privForm.sede_id,
        program_id: privForm.program_id,
        level: privForm.level || null,
        teacher_id: privForm.teacher_id || null,
        is_private: true,
        max_students: privForm.student_ids.length === 1 ? 1 : 2,
        schedule: autoLabel || "",
        schedule_days: privForm.schedule_days,
        schedule_start_time: privForm.schedule_start_time || null,
        schedule_end_time: privForm.schedule_end_time || null,
        schedule_times_by_day: privForm.schedule_times_by_day || null,
        meet_platform: privForm.meet_platform || null,
      };
      const customAmount = privForm.custom_price !== "" && privForm.custom_price != null
        ? Number(privForm.custom_price)
        : null;
      const customCycle = privForm.custom_cycle !== "" && privForm.custom_cycle != null
        ? Number(privForm.custom_cycle)
        : null;
      const startsOnDay = privForm.starts_on_day !== "" && privForm.starts_on_day != null
        ? Number(privForm.starts_on_day)
        : null;
      const paidOnStart = !!privForm.paid_on_start && !privForm.scholarship;
      const excludeFromRevenue = paidOnStart && !!privForm.exclude_from_revenue;
      const becado = !!privForm.scholarship;
      const ciclosBecados = becado ? (parseInt(privForm.scholarship_cycles, 10) || 1) : null;
      const paymentLink = (privForm.payment_link || "").trim() || null;
      if (editingPrivate) {
        // EDIT MODE: update group, then PATCH each existing enrollment
        await api.put(`/groups/${editingPrivate.groupId}`, payload);
        for (const eid of editingPrivate.enrollmentIds) {
          await api.patch(`/enrollments/${eid}`, {
            start_date: privForm.start_date || null,
            classes_included: customCycle,
            amount: customAmount,
            starts_on_day: startsOnDay,
            paid_on_start: paidOnStart,
            payment_link: paymentLink,
            self_booking: !!privForm.self_booking,
            scholarship: becado,
            scholarship_cycles: ciclosBecados,
            scholarship_notify: privForm.scholarship_notify !== false,
          });
        }
      } else {
        // CREATE MODE: create group + enrollments. Si falla una matrícula se
        // borra el grupo recién creado: si no, cada reintento dejaba un grupo
        // duplicado y vacío (bug reportado el 01/09/2026).
        const g = await api.post("/groups", payload);
        const gid = g.data.id;
        try {
          for (const [i, sid] of privForm.student_ids.entries()) {
            await api.post("/enrollments", {
              student_id: sid,
              group_id: gid,
              start_date: privForm.start_date || null,
              amount: customAmount,
              classes_included: customCycle,
              starts_on_day: startsOnDay,
              paid_on_start: paidOnStart,
              sale_date: privForm.sale_date || privForm.start_date || null,
              paid_date: paidOnStart ? (privForm.paid_date || today) : null,
              payment_link: paymentLink,
              exclude_from_revenue: excludeFromRevenue,
              self_booking: !!privForm.self_booking,
              send_welcome_email: privForm.send_welcome_email !== false,
              // Pareja con UNA sola factura: el primero la recibe y el segundo
              // queda listado en ella (no se emite una segunda factura).
              shared_billing: !!privForm.shared_billing && i > 0,
              scholarship: becado,
              scholarship_cycles: ciclosBecados,
              scholarship_notify: privForm.scholarship_notify !== false,
            });
          }
        } catch (e) {
          await api.delete(`/groups/${gid}`).catch(() => {});
          throw e;
        }
      }
      toast.success(t("saved"));
      setOpenPrivate(false);
      setEditingPrivate(null);
      load();
    } catch (e) {
      // Mostrar el error real: el genérico escondía fallos del servidor.
      toast.error(e?.response?.data?.detail || e?.message || t("error_generic"));
    }
  };

  const removeGroup = async (id, name) => {
    const ok = await confirm({
      title: "¿Borrar grupo?",
      description: `Se eliminará "${name || 'este grupo'}" y TODOS sus datos: asistencia, tareas, mensajes y archivos.\n\nLos pagos ya realizados se conservan por contabilidad.\n\nEsta acción no se puede deshacer. Si quieres conservar el historial, usa "Archivar" en su lugar.`,
      confirmText: "Sí, borrar todo",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/groups/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const archiveGroup = async (id, name) => {
    const ok = await confirm({
      title: "¿Archivar grupo?",
      description: `"${name || 'Este grupo'}" pasará al Historial. Se conserva todo (asistencia, tareas, pagos) y podrás reactivarlo cuando quieras.`,
      confirmText: "Sí, archivar",
    });
    if (!ok) return;
    await api.post(`/groups/${id}/archive`);
    toast.success("Grupo archivado");
    load();
  };

  const unarchiveGroup = async (id) => {
    await api.post(`/groups/${id}/unarchive`);
    toast.success("Grupo reactivado");
    load();
  };

  const convertPrivateToGroup = async (g) => {
    const ok = await confirm({
      title: "¿Convertir a grupo público?",
      description: `"${g.name}" pasará a ser un grupo público (varios estudiantes). Se conservan estudiantes matriculados, asistencia, tareas y pagos.\n\nEl límite de cupos quedará ilimitado; podrás ajustarlo después.`,
      confirmText: "Sí, convertir",
    });
    if (!ok) return;
    try {
      await api.put(`/groups/${g.id}`, {
        name: g.name,
        sede_id: g.sede_id,
        program_id: g.program_id,
        level: g.level || null,
        teacher_id: g.teacher_id || null,
        is_private: false,
        max_students: null,
        schedule: g.schedule || "",
        schedule_days: g.schedule_days || [],
        schedule_start_time: g.schedule_start_time || null,
        schedule_end_time: g.schedule_end_time || null,
        schedule_times_by_day: g.schedule_times_by_day || null,
        meet_platform: g.meet_platform || null,
      });
      toast.success("Grupo convertido a público");
      setOpenPrivate(false);
      setEditingPrivate(null);
      load();
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const enrollStudent = async () => {
    if (!enrollSelectedStudent || !enrollFor) return;
    try {
      setEnrollSubmitting(true);
      const customAmount = enrollCustomPrice !== "" && enrollCustomPrice != null
        ? Number(enrollCustomPrice)
        : null;
      const customCycle = enrollCustomCycle !== "" && enrollCustomCycle != null
        ? Number(enrollCustomCycle)
        : null;
      const startsOnDay = enrollStartsOnDay !== "" && enrollStartsOnDay != null
        ? Number(enrollStartsOnDay)
        : null;
      await api.post("/enrollments", {
        student_id: enrollSelectedStudent.user_id,
        group_id: enrollFor.id,
        start_date: enrollStartDate || null,
        amount: customAmount,
        classes_included: customCycle,
        starts_on_day: startsOnDay,
        paid_on_start: !!enrollPaidOnStart,
        sale_date: enrollSaleDate || enrollStartDate || null,
        paid_date: enrollPaidOnStart ? (enrollPaidDate || null) : null,
        shared_billing: !!enrollSharedBilling,
        self_booking: !!enrollSelfBooking,
        exclude_from_revenue: !!enrollPaidOnStart && !!enrollHistorical,
        scholarship: !!enrollScholarship,
        scholarship_cycles: enrollScholarship ? (parseInt(enrollScholarshipCycles, 10) || 1) : null,
        scholarship_notify: !!enrollScholarshipNotify,
        payment_link: (enrollPaymentLink || "").trim() || null,
        send_welcome_email: !!enrollSendWelcome,
      });
      toast.success(t("saved"));
      setOpenEnroll(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("error_generic");
      toast.error(msg);
    } finally {
      setEnrollSubmitting(false);
    }
  };

  const saveStartDate = async (enrollment_id, value) => {
    try {
      await api.patch(`/enrollments/${enrollment_id}`, { start_date: value || null });
      toast.success(t("saved"));
      setEditingStartFor(null);
      load();
    } catch {
      toast.error(t("error_generic"));
    }
  };

  // Atomic save of all three editable fields (date + payment_link + bank_account).
  // Optionally resend the welcome email so the student gets the updated payment info.
  const saveEnrollmentEdits = async (enrollment_id, alsoResend = false) => {
    try {
      const payload = {
        start_date: editingStartDraft || null,
        payment_link: editingPaymentDraft?.trim() ? editingPaymentDraft.trim() : null,
        bank_account: editingBankDraft || null,
      };
      await api.patch(`/enrollments/${enrollment_id}`, payload);
      if (alsoResend) {
        await api.post(`/enrollments/${enrollment_id}/notify`);
        toast.success(t("saved_and_email_resent"));
      } else {
        toast.success(t("saved"));
      }
      setEditingStartFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const notifyEnrollment = async (enrollment_id) => {
    try {
      await api.post(`/enrollments/${enrollment_id}/notify`);
      toast.success(t("enrollment_email_sent"));
    } catch (e) {
      const msg = e?.response?.data?.detail || t("error_generic");
      toast.error(msg);
    }
  };

  const downloadInvoice = async (enrollment_id) => {
    // OJO: `window.open` con un blob después de un `await` lo bloquea el
    // navegador (no se ve nada al tocar el botón, reportado el 11/09/2026).
    // Se descarga con un <a download>, que nunca se bloquea.
    try {
      const resp = await api.get(`/enrollments/${enrollment_id}/invoice`, {
        params: { lang: (localStorage.getItem("lang") || "es") },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${enrollment_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Factura descargada");
    } catch (e) {
      // Con responseType blob el detalle del error también viene como blob.
      let msg = t("error_generic");
      try {
        const raw = e?.response?.data;
        const text = raw instanceof Blob ? await raw.text() : null;
        msg = (text ? JSON.parse(text).detail : raw?.detail) || msg;
      } catch { /* se queda el mensaje genérico */ }
      toast.error(msg);
    }
  };

  const removeEnrollment = async (id, studentName) => {
    const result = await confirm({
      title: "¿Borrar el programa de clases?",
      description: `Se eliminará este programa de clases${studentName ? ` de ${studentName}` : ''} junto con sus registros de asistencia. Los pagos realizados se conservan por contabilidad, salvo que marques la casilla de abajo.\n\nSi quieres conservar el historial completo, usa "Archivar" en su lugar.`,
      confirmText: "Sí, borrar",
      danger: true,
      checkbox: {
        label: "También eliminar los pagos asociados (incluye los pagados). Útil para inscripciones de prueba.",
        defaultValue: false,
      },
    });
    if (!result?.ok) return;
    await api.delete(`/enrollments/${id}`, { params: { delete_paid_payments: result.checkbox ? "true" : "false" } });
    load();
  };

  const archiveEnrollment = async (enr) => {
    const student = usersMap[enr.student_id];
    const studentName = student?.name || enr.student_name || "";
    const result = await confirm({
      title: "¿Archivar el programa de clases?",
      description: `El programa de clases${studentName ? ` de ${studentName}` : ''} pasará al historial del estudiante. Se conserva todo y puedes reactivarlo cuando regrese.`,
      confirmText: "Sí, archivar",
      checkbox: {
        // Marcada por defecto: al archivar, lo esperado es no seguir cobrando
        // (decisión de la dueña, 11/09/2026).
        label: "Frenar también la facturación (no se emite la factura del ciclo siguiente)",
        defaultValue: true,
      },
    });
    if (!result?.ok) return;
    await api.post(`/enrollments/${enr.id}/archive`, null, {
      params: { suspend: result.checkbox ? "true" : "false" },
    });
    toast.success(result.checkbox ? "Programa archivado y facturación frenada" : "Programa archivado");
    load();
  };

  const unarchiveEnrollment = async (id) => {
    await api.post(`/enrollments/${id}/unarchive`);
    toast.success("Programa reactivado");
    load();
  };

  const isPrivateView = mode === "private";
  const pageTitle = isPrivateView ? t("filter_private") : mode === "group" ? t("filter_groups") : t("groups");

  return (
    <div>
      <PageHeader
        title={pageTitle}
        testId="groups-header"
        action={
          <div className="flex gap-2 items-center">
            <MemberActionsHelp />
            <Button
              data-testid="toggle-archived-view"
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? "bg-slate-700 hover:bg-slate-800" : ""}
              title={showArchived ? "Volver a activos" : "Ver archivados"}
            >
              {showArchived ? "← Volver a activos" : "📦 Archivados"}
            </Button>
            {isPrivateView ? (
              <Button data-testid="new-private-group-button" onClick={openCreatePrivate} disabled={showArchived}>
                <UserCheck className="h-4 w-4" /> {t("new_private_group")}
              </Button>
            ) : (
              <>
                <Button data-testid="new-private-group-button" onClick={openCreatePrivate} variant="outline" disabled={showArchived}>
                  <UserCheck className="h-4 w-4" /> {t("new_private_group")}
                </Button>
                <Button data-testid="new-group-button" onClick={openCreate} disabled={showArchived}>
                  <Plus className="h-4 w-4" /> {t("new_group")}
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Search + teacher filter — applied on top of sede grouping. Empty
          filters leave the original sede layout intact. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            data-testid="groups-search-input"
            type="search"
            placeholder={t("groups_search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-9"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              data-testid="groups-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100"
              aria-label={t("clear")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 whitespace-nowrap">{t("teacher")}:</Label>
          <Select value={teacherFilter} onValueChange={setTeacherFilter}>
            <SelectTrigger data-testid="groups-teacher-filter" className="h-9 min-w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              <SelectItem value="unassigned">{t("unassigned")}</SelectItem>
              {teachers.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })).map((tt) => (
                <SelectItem key={tt.user_id} value={tt.user_id}>{tt.name || tt.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(searchQuery || teacherFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearchQuery(""); setTeacherFilter("all"); }}
              data-testid="groups-filters-reset"
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" /> {t("clear_filters")}
            </Button>
          )}
        </div>
      </div>

      {/* Sede sections (collapsible) — sede filter removed in favor of collapsible sections */}

      {(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = groups.filter((g) => {
          if (mode === "private" && !g.is_private) return false;
          if (mode === "group" && g.is_private) return false;
          // En la vista de archivados: el grupo archivado, o el grupo activo que
          // tenga alguna matrícula archivada (si no, no habría cómo reactivarla).
          if (showArchived) {
            const hasArchivedEnr = enrollments.some((e) => e.group_id === g.id);
            if (!g.archived_at && !hasArchivedEnr) return false;
          }
          // Teacher filter
          if (teacherFilter === "unassigned") {
            if (g.teacher_id) return false;
          } else if (teacherFilter !== "all") {
            if (g.teacher_id !== teacherFilter) return false;
          }
          // Text search: match group name, program name, teacher name,
          // or ANY enrolled student's name — so admins can find a group
          // just by typing the student's first name.
          if (q) {
            const groupName = (g.name || "").toLowerCase();
            const progName = (programsMap[g.program_id] || "").toLowerCase();
            const teacherName = (usersMap[g.teacher_id]?.name || "").toLowerCase();
            const studentNames = enrollments
              .filter((e) => e.group_id === g.id)
              .map((e) => (usersMap[e.student_id]?.name || e.name || "").toLowerCase());
            const hay = [groupName, progName, teacherName, ...studentNames].some((s) => s.includes(q));
            if (!hay) return false;
          }
          return true;
        });
        if (filtered.length === 0) {
          const hasFilters = q || teacherFilter !== "all";
          return (
            <div className="text-center text-slate-500 py-12 bg-white border border-slate-200 rounded-lg" data-testid="groups-empty-state">
              {hasFilters ? (
                <div className="space-y-2">
                  <p className="text-sm">{t("no_results_for_filter")}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSearchQuery(""); setTeacherFilter("all"); }}
                    className="text-xs"
                  >
                    <X className="h-3.5 w-3.5" /> {t("clear_filters")}
                  </Button>
                </div>
              ) : t("no_data")}
            </div>
          );
        }
        // Group by sede_id; preserve sede order from sedes[]
        const bySede = new Map();
        for (const s of sedes) bySede.set(s.id, []);
        const orphans = [];
        for (const g of filtered) {
          if (bySede.has(g.sede_id)) bySede.get(g.sede_id).push(g);
          else orphans.push(g);
        }
        const sections = [];
        const _sortByName = (a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" });
        for (const s of sedes) {
          const list = (bySede.get(s.id) || []).slice().sort(_sortByName);
          if (list.length > 0) sections.push({ id: s.id, name: s.name, groups: list });
        }
        if (orphans.length > 0) sections.push({ id: "__orphan__", name: t("sede"), groups: orphans.slice().sort(_sortByName) });

        const renderCard = (g) => {
          const enr = groupEnrollments(g.id);
          const cap = g.max_students;
          // Los programas archivados siguen ocupando su lugar (el backend también
          // los cuenta): una clase privada individual no se puede reasignar a otro
          // estudiante sin antes reactivar o borrar el archivado.
          const archivedHere = archivedCounts[g.id] || 0;
          const occupied = enr.length + (showArchived ? 0 : archivedHere);
          const isFull = cap && occupied >= cap;
          return (
            <Card key={g.id} className="p-5 border-slate-200 hover:border-slate-300 transition-colors" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{g.name}</h3>
                    {g.is_private && (
                      <Badge variant="secondary" className="bg-brand/10 text-brand border-brand/30">
                        {t("private")}{cap ? ` · ${cap === 1 ? t("private_individual") : t("private_pair")}` : ""}
                      </Badge>
                    )}
                    {isFull && (
                      <Badge variant="outline" className="border-red-300 text-red-600">
                        {t("capacity_reached")}
                      </Badge>
                    )}
                    {!g.class_meet_link && (
                      <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50" title={t("group_no_meet_link_hint")} data-testid={`no-meet-link-${g.id}`}>
                        ⚠ {t("group_no_meet_link")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {programsMap[g.program_id]}
                    {g.level && (
                      <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold tracking-wide bg-brand-blue/10 text-brand-blue"
                            title={t("level_hint")} data-testid={`group-level-${g.id}`}>
                        {g.level}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t("teacher")}: <span className="text-slate-900">{usersMap[g.teacher_id]?.name || "—"}</span>
                  </p>
                  {(g.schedule_days?.length || g.schedule_start_time || g.schedule) && (
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <CalendarClock className="h-3 w-3 text-brand-blue" />
                      <span>{buildScheduleLabel(g.schedule_days, g.schedule_start_time, g.schedule_end_time, t, g.schedule_times_by_day) || g.schedule}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => navigate(`/dashboard/notebooks?group=${g.id}`)}
                    data-testid={`notebook-button-${g.id}`}
                  >
                    <NotebookPen className="h-3.5 w-3.5" /> Notebook
                  </Button>
                  {showArchived ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => unarchiveGroup(g.id)}
                      className="text-emerald-600"
                      title="Reactivar"
                      data-testid={`unarchive-group-${g.id}`}
                    >
                      ↩️
                    </Button>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => g.is_private ? openEditPrivate(g) : openEdit(g)} data-testid={`edit-group-${g.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => archiveGroup(g.id, g.name)}
                        className="text-slate-600"
                        title="Archivar"
                        data-testid={`archive-group-${g.id}`}
                      >
                        📦
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => removeGroup(g.id, g.name)} className="text-red-600" data-testid={`delete-group-${g.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                    <UsersIcon className="h-3.5 w-3.5" />
                    {t("members")} ({enr.length}{cap ? `/${cap}` : ""})
                    {!showArchived && archivedHere > 0 && (
                      <span className="normal-case tracking-normal text-amber-700">
                        · 📦 {archivedHere} archivado{archivedHere > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`enroll-button-${g.id}`}
                    disabled={isFull}
                    title={
                      isFull && enr.length === 0 && archivedHere > 0
                        ? "El lugar de esta clase lo ocupa un programa archivado. Reactivalo (📦 Archivados) o borralo antes de inscribir a otro estudiante."
                        : undefined
                    }
                    onClick={() => {
                      setEnrollFor(g);
                      const prog = programs.find((p) => p.id === g.program_id);
                      setEnrollSelfBooking(!!(g.is_private && prog?.self_booking));
                      setEnrollSendWelcome(true);
                      setEnrollSaleDate("");
                      setEnrollPaidDate(today);
                      setOpenEnroll(true);
                    }}
                  >
                    <UserPlus className="h-3.5 w-3.5" /> {t("enroll_student")}
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {enr.length === 0 && (
                    archivedCounts[g.id] ? (
                      <button
                        type="button"
                        onClick={() => setShowArchived(true)}
                        className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 hover:bg-amber-100 transition-colors text-left"
                        data-testid={`group-has-archived-hint-${g.id}`}
                      >
                        📦 {archivedCounts[g.id]} programa(s) archivado(s) en este grupo — ver Archivados
                      </button>
                    ) : (
                      <p className="text-xs text-slate-400">{t("no_data")}</p>
                    )
                  )}
                  {enr.map((e) => {
                    const isEditingStart = editingStartFor === e.id;
                    const c = e.consumption;
                    const hasProgress = c && c.total > 0;
                    const pctColor = !hasProgress ? "bg-slate-300"
                      : c.pct >= 100 ? "bg-emerald-500"
                      : c.pct >= 75 ? "bg-orange-500"
                      : c.pct >= 50 ? "bg-brand-blue"
                      : "bg-slate-400";
                    return (
                      <div key={e.id} className={`flex items-center justify-between gap-2 text-sm px-3 py-1.5 rounded ${e.scholarship ? "bg-violet-50 border-l-2 border-violet-300" : "bg-slate-50"}`} title={e.scholarship ? `Beca${e.scholarship_cycles ? ` por ${e.scholarship_cycles} ciclo(s)` : ""}: el cobro sale en $0` : undefined} data-testid={`member-row-${e.id}`}>
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate flex items-center gap-1.5">
                              {usersMap[e.student_id]?.name || e.student_id}
                              {e.suspended && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200"
                                  title={t("suspended_hint")}
                                  data-testid={`member-suspended-badge-${e.id}`}
                                >
                                  <Pause className="h-2.5 w-2.5" /> {t("suspended")}
                                </span>
                              )}
                              {/* Etiqueta "sigue facturando": pendiente de autorización (ver
                                  memory/pendiente_autorizacion.md) */}
                              {e.self_booking && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200"
                                  title={t("self_booking_hint")}
                                  data-testid={`member-self-booking-badge-${e.id}`}
                                >
                                  {t("self_booking_badge")}
                                </span>
                              )}
                            </span>
                            {hasProgress && (
                              <span
                                className={`text-[10px] font-semibold tracking-wide ${
                                  c.pct >= 100 ? "text-emerald-700"
                                    : c.pct >= 75 ? "text-orange-700"
                                    : "text-slate-600"
                                }`}
                                data-testid={`member-consumption-${e.id}`}
                              >
                                {c.consumed}/{c.total} · {c.pct}%
                              </span>
                            )}
                          </div>
                          {hasProgress && (
                            <div className="h-1 mt-1.5 bg-slate-200 rounded-full overflow-hidden" title={`Justificadas usadas: ${c.free_used}/${c.threshold}`}>
                              <div
                                className={`h-full ${pctColor} transition-all`}
                                style={{ width: `${Math.min(c.pct, 100)}%` }}
                              />
                            </div>
                          )}
                          {isEditingStart ? (
                            <div className="flex flex-col gap-1.5 mt-1.5">
                              <Input
                                type="date"
                                data-testid={`member-start-date-input-${e.id}`}
                                value={editingStartDraft}
                                onChange={(ev) => setEditingStartDraft(ev.target.value)}
                                className="h-7 text-xs"
                              />
                              <Input
                                type="url"
                                placeholder={t("payment_link") + " (https://...)"}
                                data-testid={`member-payment-link-input-${e.id}`}
                                value={editingPaymentDraft}
                                onChange={(ev) => setEditingPaymentDraft(ev.target.value)}
                                className="h-7 text-xs"
                              />
                              <Select
                                value={editingBankDraft || "__none__"}
                                onValueChange={(v) => setEditingBankDraft(v === "__none__" ? "" : v)}
                              >
                                <SelectTrigger
                                  data-testid={`member-bank-account-select-${e.id}`}
                                  className="h-7 text-xs"
                                >
                                  <SelectValue placeholder={t("bank_account_select_placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">{t("bank_account_none")}</SelectItem>
                                  {BANK_ACCOUNTS.map((b) => (
                                    <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                <Button
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  data-testid={`member-edit-save-${e.id}`}
                                  onClick={() => saveEnrollmentEdits(e.id, false)}
                                >
                                  {t("save_all")}
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 px-2 text-xs bg-brand-orange hover:bg-brand-orange/90 text-white whitespace-nowrap"
                                  data-testid={`member-edit-save-resend-${e.id}`}
                                  onClick={() => saveEnrollmentEdits(e.id, true)}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  {t("save_and_resend_email")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => setEditingStartFor(null)}
                                >
                                  {t("cancel")}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              data-testid={`member-start-date-edit-${e.id}`}
                              onClick={() => { setEditingStartFor(e.id); setEditingStartDraft(e.start_date || today); setEditingPaymentDraft(e.payment_link || ""); setEditingBankDraft(e.bank_account || ""); }}
                              className="text-[11px] mt-1 text-left flex items-center gap-1.5 text-slate-500 hover:text-brand-blue transition-colors"
                              title={t("edit_start_date")}
                            >
                              <CalendarClock className="h-3 w-3" />
                              {e.start_date ? (
                                <span>{t("start_date")}: <span className="text-slate-700">{formatDMY(e.start_date, lang)}</span></span>
                              ) : (
                                <span className="italic">{t("no_start_date")}</span>
                              )}
                              {e.payment_link && <span className="text-brand-blue">· 💳</span>}
                              <Pencil className="h-2.5 w-2.5 opacity-50" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {g.class_meet_link && (
                            <a
                              href={g.class_meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(ev) => ev.stopPropagation()}
                              className="inline-flex items-center justify-center h-7 w-7 rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title={g.class_meet_platform === "zoom" ? t("start_class_zoom") : g.class_meet_platform === "meet" ? t("start_class_meet") : t("start_class")}
                              data-testid={`member-start-class-${e.id}`}
                            >
                              {g.class_meet_platform === "meet" ? (
                                <MeetIcon className="h-3.5 w-3.5" />
                              ) : g.class_meet_platform === "zoom" ? (
                                <ZoomIcon className="h-3.5 w-3.5 rounded-sm" />
                              ) : (
                                <Video className="h-3.5 w-3.5" />
                              )}
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => notifyEnrollment(e.id)}
                            className="text-brand-blue h-7 px-2"
                            title={t("resend_enrollment_email")}
                            data-testid={`member-notify-${e.id}`}
                          >
                            <Mail className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(e.id)}
                            className="text-emerald-700 h-7 px-2"
                            title={t("download_invoice")}
                            data-testid={`member-invoice-${e.id}`}
                          >
                            <FileText className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const prog = programs.find((p) => p.id === g.program_id);
                              const totalPerCycle = e.classes_included || prog?.classes_included || 8;
                              setAttViewer({
                                student: { student_id: e.student_id, name: usersMap[e.student_id]?.name || e.student_id },
                                group: { ...g, program_name: prog?.name },
                                enrollment: e,
                                totalPerCycle,
                              });
                            }}
                            className="text-slate-700 h-7 px-2"
                            title="Ver asistencia del estudiante"
                            data-testid={`member-view-attendance-${e.id}`}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditEnrollment(e)}
                            className="text-brand-blue h-7 px-2"
                            title={t("edit_enrollment")}
                            data-testid={`member-edit-enrollment-${e.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPlanTarget(e)}
                            className="text-violet-700 h-7 px-2"
                            title={t("change_plan")}
                            data-testid={`member-change-plan-${e.id}`}
                          >
                            <ArrowLeftRight className="h-3 w-3" />
                          </Button>
                          {/* Suspensión de COBRO. Activa = fondo ámbar fuerte. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSuspendTarget(e)}
                            className={e.suspended
                              ? "h-7 px-2 bg-amber-500 text-white hover:bg-amber-600 ring-2 ring-amber-300"
                              : "text-amber-600 h-7 px-2"}
                            title={e.suspended
                              ? (e.suspended_until ? `Cobro suspendido hasta ${formatDMY(e.suspended_until, lang)}` : "Cobro suspendido (indefinido)")
                              : t("suspend")}
                            data-testid={`member-suspend-${e.id}`}
                          >
                            {e.suspended ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                          </Button>
                          {/* Suspensión de ASISTENCIA Y COBRO (viaje). Activa = rojo fuerte. */}
                          {isSuper && (() => {
                            const pauseActive = (e.pauses || []).some(
                              (p) => !p.to_date || p.to_date >= todayISO(),
                            );
                            const activePause = (e.pauses || []).find(
                              (p) => !p.to_date || p.to_date >= todayISO(),
                            );
                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPauseTarget(e)}
                                className={pauseActive
                                  ? "h-7 px-2 bg-red-600 text-white hover:bg-red-700 ring-2 ring-red-300"
                                  : "text-amber-700 h-7 px-2"}
                                title={pauseActive
                                  ? `En pausa desde ${formatDMY(activePause.from_date, lang)}${activePause.to_date ? ` hasta ${formatDMY(activePause.to_date, lang)}` : " (sin fecha de regreso)"}`
                                  : "Suspender asistencia y cobro (viaje)"}
                                data-testid={`member-pause-${e.id}`}
                              >
                                <PauseCircle className="h-3 w-3" />
                              </Button>
                            );
                          })()}
                          {showArchived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => unarchiveEnrollment(e.id)}
                              className="text-emerald-700 h-7 px-2"
                              title="Reactivar programa de clases"
                              data-testid={`unarchive-enrollment-${e.id}`}
                            >
                              ↩️
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => archiveEnrollment(e)}
                              className="text-slate-600 h-7 px-2"
                              title="Archivar programa de clases"
                              data-testid={`archive-enrollment-${e.id}`}
                            >
                              📦
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => removeEnrollment(e.id, e.student_name)} className="text-red-600 h-7 px-2" title={t("remove")}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          );
        };

        return (
          <div className="space-y-6">
            {sections.map((section) => {
              // Con un filtro activo se abren solas: si no, buscar un alumno no
              // mostraría nada y parecería que no existe.
              const hayFiltro = !!q || teacherFilter !== "all";
              const isCollapsed = !hayFiltro && !expandedSedes[section.id];
              return (
                <div key={section.id} data-testid={`sede-section-${section.id}`}>
                  <button
                    type="button"
                    data-testid={`sede-header-${section.id}`}
                    onClick={() => setExpandedSedes({ ...expandedSedes, [section.id]: isCollapsed })}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors mb-3"
                  >
                    <div className="flex items-center gap-2.5">
                      <MapPin className="h-4 w-4 text-brand-blue" />
                      <span className="font-display text-sm font-semibold text-slate-900">{section.name}</span>
                      <span className="text-xs text-slate-500">· {section.groups.length} {(section.groups.length === 1 ? t("group") : t("groups")).toLowerCase()}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                  {!isCollapsed && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {section.groups.map(renderCard)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Admin: student attendance viewer */}
      {attViewer && (
        <StudentAttendanceDialog
          open={!!attViewer}
          onOpenChange={(o) => { if (!o) setAttViewer(null); }}
          student={attViewer.student}
          group={attViewer.group}
          enrollment={attViewer.enrollment}
          totalPerCycle={attViewer.totalPerCycle}
          onChanged={() => load()}
        />
      )}

      {planTarget && (() => {
        const pg = groups.find((g) => g.id === planTarget.group_id);
        return (
          <ChangePlanDialog
            open={!!planTarget}
            onOpenChange={(o) => { if (!o) setPlanTarget(null); }}
            enrollment={planTarget}
            program={programs.find((p) => p.id === pg?.program_id)}
            studentName={usersMap[planTarget.student_id]?.name || planTarget.student_id}
            onSaved={() => load()}
          />
        );
      })()}

      {suspendTarget && (
        <SuspendBillingDialog
          open={!!suspendTarget}
          onOpenChange={(o) => { if (!o) setSuspendTarget(null); }}
          enrollment={suspendTarget}
          studentName={usersMap[suspendTarget.student_id]?.name || suspendTarget.student_id}
          onSaved={() => load()}
        />
      )}

      {pauseTarget && (
        <PauseEnrollmentDialog
          open={!!pauseTarget}
          onOpenChange={(o) => { if (!o) setPauseTarget(null); }}
          enrollment={pauseTarget}
          studentName={usersMap[pauseTarget.student_id]?.name || pauseTarget.student_id}
          onSaved={() => load()}
        />
      )}

      {/* Create/Edit group dialog */}
      <Dialog open={openGroup} onOpenChange={setOpenGroup}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_group")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("name")}</Label>
              <Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("sede")}</Label>
                <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                  <SelectTrigger data-testid="group-sede-select"><SelectValue placeholder={t("select_sede")} /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("program")}</Label>
                <Select value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v })}>
                  <SelectTrigger data-testid="group-program-select"><SelectValue placeholder={t("select_program")} /></SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("field_level")}</Label>
              <Select value={form.level || "none"} onValueChange={(v) => setForm({ ...form, level: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="group-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("level_none")}</SelectItem>
                  {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">{t("level_hint")}</p>
            </div>
            <div>
              <Label>{t("teacher")}</Label>
              <Select value={form.teacher_id || "none"} onValueChange={(v) => setForm({ ...form, teacher_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="group-teacher-select"><SelectValue placeholder={t("select_teacher")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {teachers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("field_meet_platform")}</Label>
              <Select value={form.meet_platform || "__default__"} onValueChange={(v) => setForm({ ...form, meet_platform: v === "__default__" ? "" : v })}>
                <SelectTrigger data-testid="group-meet-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">{t("platform_default")}</SelectItem>
                  <SelectItem value="meet">{t("platform_meet")}</SelectItem>
                  <SelectItem value="zoom">{t("platform_zoom")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-brand-blue" /> {t("schedule")}
              </Label>
              <div className="mt-2">
                <ScheduleEditor
                  testIdPrefix="group-schedule"
                  days={form.schedule_days}
                  startTime={form.schedule_start_time}
                  endTime={form.schedule_end_time}
                  timesByDay={form.schedule_times_by_day}
                  onChange={({ days, startTime, endTime, timesByDay }) =>
                    setForm({ ...form, schedule_days: days, schedule_start_time: startTime, schedule_end_time: endTime, schedule_times_by_day: timesByDay })
                  }
                />
              </div>
            </div>
            <div>
              <Label>{t("max_students")}</Label>
              <Input
                type="number"
                min="1"
                data-testid="group-max-students-input"
                value={form.max_students}
                onChange={(e) => setForm({ ...form, max_students: e.target.value })}
                placeholder="∞"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("max_students_hint")}</p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <Label className="m-0">{t("is_private")}</Label>
              <Switch data-testid="group-private-switch" checked={form.is_private} onCheckedChange={(v) => setForm({ ...form, is_private: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenGroup(false)}>{t("cancel")}</Button>
            <Button data-testid="save-group-button" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll student dialog */}
      <Dialog
        open={openEnroll}
        onOpenChange={(o) => {
          setOpenEnroll(o);
          if (o) {
            setEnrollStartDate(today);
            setEnrollStudentQuery("");
            setEnrollSelectedStudent(null);
            setEnrollCustomPrice("");
            setEnrollCustomCycle("");
            setEnrollStartsOnDay("");
            setEnrollPaidOnStart(false);
            setEnrollScholarship(false);
            setEnrollScholarshipCycles("1");
            setEnrollScholarshipNotify(true);
            setEnrollSharedBilling(false);
            setEnrollPaymentLink("");
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("enroll_student")}</DialogTitle></DialogHeader>

          <div className="space-y-4">
            {/* Nombre: search if no student, card if selected */}
            <div>
              <Label className="text-xs">{t("name")}</Label>
              {!enrollSelectedStudent ? (
                <>
                  <Input
                    type="search"
                    placeholder={t("student_search_placeholder")}
                    value={enrollStudentQuery}
                    onChange={(e) => setEnrollStudentQuery(e.target.value)}
                    data-testid="enroll-student-search"
                    className="mt-1.5"
                  />
                  {(() => {
                    const q = enrollStudentQuery.trim().toLowerCase();
                    const enrolledIds = new Set(
                      enrollments
                        .filter((e) => enrollFor && e.group_id === enrollFor.id)
                        .map((e) => e.student_id)
                    );
                    const matches = students
                      .filter((s) => !enrolledIds.has(s.user_id))
                      .filter((s) => {
                        if (!q) return true;
                        return (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
                      })
                      .slice(0, 6);
                    return (
                      <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto" data-testid="enroll-student-results">
                        {matches.length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">
                            {q ? t("student_picker_no_match") : t("no_data")}
                          </p>
                        ) : (
                          matches.map((s) => (
                            <button
                              key={s.user_id}
                              type="button"
                              data-testid={`enroll-student-${s.user_id}`}
                              onClick={() => setEnrollSelectedStudent(s)}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-slate-100 text-left border border-slate-200 transition-colors"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{s.name}</div>
                                <div className="text-xs text-slate-500 truncate">{s.email}</div>
                              </div>
                              <UserPlus className="h-4 w-4 text-slate-400 shrink-0" />
                            </button>
                          ))
                        )}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="mt-1.5 flex items-center justify-between p-3 border border-slate-200 rounded-md bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="enroll-selected-student-name">{enrollSelectedStudent.name}</div>
                    <div className="text-xs text-slate-500 truncate">{enrollSelectedStudent.email}</div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEnrollSelectedStudent(null)}
                    data-testid="enroll-change-student-btn"
                  >
                    {t("change")}
                  </Button>
                </div>
              )}
            </div>

            {/* Pricing card — always visible */}
            {(() => {
              const groupProgramId = enrollFor?.program_id;
              const selectedProg = programs.find((p) => p.id === groupProgramId);
              const defaultPrice = selectedProg?.price;
              const defaultCycle = selectedProg?.classes_included;
              return (
                <div className="p-3 bg-amber-50/60 rounded-md border border-amber-200/60 space-y-2.5" data-testid="enroll-pricing-card">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      {t("program_price")}
                    </span>
                    <span className="text-sm font-mono font-semibold text-slate-900" data-testid="enroll-program-price">
                      {defaultPrice != null && defaultPrice !== ""
                        ? <>$ {Number(defaultPrice).toFixed(2)} <span className="text-[10px] text-slate-500 font-sans">USD</span></>
                        : <em className="text-slate-400 text-xs not-italic font-sans">{t("program_price_none")}</em>}
                    </span>
                  </div>
                  <div>
                    <Label className="text-xs">{t("custom_price")}</Label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        data-testid="enroll-custom-price-input"
                        placeholder={defaultPrice != null ? String(defaultPrice) : "0.00"}
                        value={enrollCustomPrice}
                        onChange={(e) => setEnrollCustomPrice(e.target.value)}
                        className="pl-7 pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{t("custom_price_hint")}</p>
                  </div>
                  <div className="pt-2 border-t border-amber-200/60">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        {t("program_cycle")}
                      </span>
                      <span className="text-sm font-mono font-semibold text-slate-900" data-testid="enroll-program-cycle">
                        {defaultCycle != null && defaultCycle !== ""
                          ? <>{defaultCycle} <span className="text-[10px] text-slate-500 font-sans">{t("sessions_unit")}</span></>
                          : <em className="text-slate-400 text-xs not-italic font-sans">—</em>}
                      </span>
                    </div>
                    <Label className="text-xs">{t("custom_cycle")}</Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      data-testid="enroll-custom-cycle-input"
                      placeholder={defaultCycle != null ? String(defaultCycle) : "0"}
                      value={enrollCustomCycle}
                      onChange={(e) => setEnrollCustomCycle(e.target.value)}
                      className="mt-1.5"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">{t("custom_cycle_hint")}</p>
                  </div>
                  <div className="pt-2 border-t border-amber-200/60">
                    <Label className="text-xs">{t("starts_on_day")}</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      data-testid="enroll-starts-on-day-input"
                      placeholder="0"
                      value={enrollStartsOnDay}
                      onChange={(e) => setEnrollStartsOnDay(e.target.value)}
                      className="mt-1.5"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">{t("starts_on_day_hint")}</p>
                  </div>
                  <div className="pt-2 border-t border-amber-200/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <Label htmlFor="enroll-scholarship" className="text-xs">Beca (no se le cobra)</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          El programa mantiene su precio, pero el cobro sale en $0 y ya saldado. Sin factura ni correo de pago.
                        </p>
                      </div>
                      <Switch
                        id="enroll-scholarship"
                        data-testid="enroll-scholarship-switch"
                        checked={enrollScholarship}
                        onCheckedChange={(v) => {
                          setEnrollScholarship(v);
                          if (v) {
                            setEnrollPaidOnStart(false);
                            setEnrollHistorical(false);
                          }
                        }}
                      />
                    </div>
                    {enrollScholarship && (
                      <div className="mt-2">
                        <Label htmlFor="enroll-scholarship-cycles" className="text-xs">
                          ¿Cuántos ciclos becados?
                        </Label>
                        <Input
                          id="enroll-scholarship-cycles"
                          type="number"
                          step="1"
                          min="1"
                          data-testid="enroll-scholarship-cycles-input"
                          value={enrollScholarshipCycles}
                          onChange={(e) => setEnrollScholarshipCycles(e.target.value)}
                          className="mt-1.5"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Al terminar esos ciclos, la renovación vuelve a salir al precio normal.
                        </p>
                        <div className="flex items-center justify-between gap-3 mt-2">
                          <Label htmlFor="enroll-scholarship-notify" className="text-xs font-normal">
                            Avisarle por correo que la escuela le cubre las clases
                          </Label>
                          <Switch
                            id="enroll-scholarship-notify"
                            data-testid="enroll-scholarship-notify-switch"
                            checked={enrollScholarshipNotify}
                            onCheckedChange={setEnrollScholarshipNotify}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="pt-2 border-t border-amber-200/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <Label htmlFor="enroll-paid-on-start" className="text-xs">{t("paid_on_start")}</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">{t("paid_on_start_hint")}</p>
                      </div>
                      <Switch
                        id="enroll-paid-on-start"
                        data-testid="enroll-paid-on-start-switch"
                        checked={enrollPaidOnStart}
                        disabled={enrollScholarship}
                        onCheckedChange={(v) => {
                          setEnrollPaidOnStart(v);
                          if (!v) setEnrollHistorical(false);
                        }}
                      />
                    </div>
                  </div>
                  {enrollPaidOnStart && (
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <Label htmlFor="enroll-historical" className="text-xs">{t("historical_payment")}</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">{t("historical_payment_hint")}</p>
                        </div>
                        <Switch
                          id="enroll-historical"
                          data-testid="enroll-historical-switch"
                          checked={enrollHistorical}
                          onCheckedChange={setEnrollHistorical}
                        />
                      </div>
                    </div>
                  )}
                  <div className="pt-2 border-t border-amber-200/60 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Fecha de venta</Label>
                      <Input
                        type="date"
                        data-testid="enroll-sale-date-input"
                        value={enrollSaleDate || enrollStartDate || ""}
                        onChange={(e) => setEnrollSaleDate(e.target.value)}
                        className="mt-1.5"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">En qué mes cuenta la venta</p>
                    </div>
                    {enrollPaidOnStart && (
                      <div>
                        <Label className="text-xs">Fecha de pago</Label>
                        <Input
                          type="date"
                          data-testid="enroll-paid-date-input"
                          value={enrollPaidDate}
                          onChange={(e) => setEnrollPaidDate(e.target.value)}
                          className="mt-1.5"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">En qué mes entra la plata</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-amber-200/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <Label htmlFor="enroll-shared-billing" className="text-xs">Facturación compartida (grupo privado)</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">Actívalo cuando dos estudiantes comparten UNA sola factura del programa. Ambos verán el mismo pago pendiente; en cuanto uno pague, se marca pagada para los dos y se muestra quién pagó.</p>
                      </div>
                      <Switch
                        id="enroll-shared-billing"
                        data-testid="enroll-shared-billing-switch"
                        checked={enrollSharedBilling}
                        onCheckedChange={setEnrollSharedBilling}
                      />
                    </div>
                  </div>
                  {enrollFor?.is_private && (
                  <div className="pt-2 border-t border-amber-200/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <Label htmlFor="enroll-self-booking" className="text-xs">{t("self_booking")}</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">{t("self_booking_hint")}</p>
                      </div>
                      <Switch
                        id="enroll-self-booking"
                        data-testid="enroll-self-booking-switch"
                        checked={enrollSelfBooking}
                        onCheckedChange={setEnrollSelfBooking}
                      />
                    </div>
                  </div>
                  )}
                </div>
              );
            })()}

            {/* Payment link — used on the welcome/invoice email as "Pagar ahora" */}
            <div>
              <Label className="text-xs">{t("payment_link")}</Label>
              <Input
                type="url"
                placeholder="https://..."
                value={enrollPaymentLink}
                onChange={(e) => setEnrollPaymentLink(e.target.value)}
                className="mt-1.5"
                data-testid="enroll-payment-link-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("priv_payment_link_hint")}</p>
            </div>

            {/* Correo de bienvenida opcional: la factura se emite igual */}
            <div className="rounded-md border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label htmlFor="enroll-send-welcome" className="text-xs">Enviar correo de bienvenida</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {enrollScholarship
                      ? "Con beca no se emite factura: el correo solo le da la bienvenida y su acceso al panel."
                      : "Si lo apagás, la factura del ciclo 1 se emite igual (queda en Mis pagos), solo no se envía el correo."}
                  </p>
                </div>
                <Switch
                  id="enroll-send-welcome"
                  data-testid="enroll-send-welcome-switch"
                  checked={enrollSendWelcome}
                  onCheckedChange={setEnrollSendWelcome}
                />
              </div>
            </div>

            {/* Start date — always visible; can be left blank when unknown */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-brand-blue" />
                {t("start_date")}
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  type="date"
                  data-testid="enroll-start-date-input"
                  value={enrollStartDate}
                  onChange={(e) => setEnrollStartDate(e.target.value)}
                  className="flex-1"
                />
                {enrollStartDate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEnrollStartDate("")}
                    data-testid="enroll-clear-start-date"
                    title="Dejar sin definir — horario pendiente"
                  >
                    Sin definir
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">{t("start_date_hint")}</p>
              {!enrollStartDate && (
                <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="enroll-start-date-empty-hint">
                  🕒 El correo dirá que el horario está pendiente de asignación.
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpenEnroll(false)} data-testid="enroll-cancel-btn">
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="enroll-preview-email-btn"
              disabled={!enrollSelectedStudent || !enrollFor}
              onClick={() => openWelcomePreview({
                student_id: enrollSelectedStudent?.user_id,
                student_name: enrollSelectedStudent?.name,
                draft: [{
                  group_id: enrollFor.id,
                  start_date: enrollStartDate || null,
                  payment_link: (enrollPaymentLink || "").trim() || enrollFor.payment_link || null,
                  bank_account: enrollFor.bank_account || null,
                  amount: enrollCustomPrice !== "" ? Number(enrollCustomPrice) : null,
                  classes_included: enrollCustomCycle !== "" ? Number(enrollCustomCycle) : null,
                  starts_on_day: enrollStartsOnDay !== "" ? Number(enrollStartsOnDay) : null,
                  paid_on_start: !!enrollPaidOnStart,
                  shared_billing: !!enrollSharedBilling,
                }],
              })}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              {t("preview_welcome_email")}
            </Button>
            <Button
              onClick={enrollStudent}
              disabled={!enrollSelectedStudent || enrollSubmitting}
              data-testid="enroll-submit-btn"
            >
              {enrollSubmitting ? "…" : t("enroll_student")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit existing Enrollment dialog — lets admins fix mistakes made during enrollment */}
      <Dialog
        open={openEditEnroll}
        onOpenChange={(o) => {
          setOpenEditEnroll(o);
          if (!o) setEditEnrollTarget(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-enrollment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-brand-blue" />
              {t("edit_enrollment")}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editEnrollTarget?.student_name || ""}
            </DialogDescription>
          </DialogHeader>
          {editEnrollTarget && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-200 bg-slate-50">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                {t("program_status")}
              </span>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                  editEnrollTarget.archived_at
                    ? "bg-slate-200 text-slate-700 border-slate-300"
                    : editEnrollTarget.suspended
                      ? "bg-amber-100 text-amber-700 border-amber-200"
                      : "bg-emerald-100 text-emerald-700 border-emerald-200"
                }`}
                data-testid="edit-enroll-program-status"
              >
                {editEnrollTarget.archived_at
                  ? t("archived")
                  : editEnrollTarget.suspended
                    ? t("suspended")
                    : t("status_active")}
              </span>
            </div>
          )}
          {editEnrollTarget && (() => {
            const targetGroup = groups.find((g) => g.id === editEnrollTarget.group_id);
            const selectedProg = programs.find((p) => p.id === targetGroup?.program_id);
            const defaultPrice = selectedProg?.price;
            const defaultCycle = selectedProg?.classes_included;
            return (
              <div className="p-3 bg-amber-50/60 rounded-md border border-amber-200/60 space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                    {t("program_price")}
                  </span>
                  <span className="text-sm font-mono font-semibold text-slate-900" data-testid="edit-enroll-program-price">
                    {defaultPrice != null && defaultPrice !== ""
                      ? <>$ {Number(defaultPrice).toFixed(2)} <span className="text-[10px] text-slate-500 font-sans">USD</span></>
                      : <em className="text-slate-400 text-xs not-italic font-sans">{t("program_price_none")}</em>}
                  </span>
                </div>
                <div>
                  <Label className="text-xs">{t("custom_price")}</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="edit-enroll-custom-price-input"
                      placeholder={defaultPrice != null ? String(defaultPrice) : "0.00"}
                      value={editEnrollForm.custom_price}
                      onChange={(ev) => setEditEnrollForm({ ...editEnrollForm, custom_price: ev.target.value })}
                      className="pl-7 pr-12"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{t("custom_price_edit_hint")}</p>
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      {t("program_cycle")}
                    </span>
                    <span className="text-sm font-mono font-semibold text-slate-900" data-testid="edit-enroll-program-cycle">
                      {defaultCycle != null && defaultCycle !== ""
                        ? <>{defaultCycle} <span className="text-[10px] text-slate-500 font-sans">{t("sessions_unit")}</span></>
                        : <em className="text-slate-400 text-xs not-italic font-sans">—</em>}
                    </span>
                  </div>
                  <Label className="text-xs">{t("custom_cycle")}</Label>
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    data-testid="edit-enroll-custom-cycle-input"
                    placeholder={defaultCycle != null ? String(defaultCycle) : "0"}
                    value={editEnrollForm.custom_cycle}
                    onChange={(ev) => setEditEnrollForm({ ...editEnrollForm, custom_cycle: ev.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">{t("custom_cycle_hint")}</p>
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-violet-200 bg-violet-50 p-2">
                    <p className="text-[11px] text-violet-800">{t("custom_cycle_edit_warn")}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0 border-violet-300 text-violet-800"
                      onClick={() => { setOpenEditEnroll(false); setPlanTarget(editEnrollTarget); }}
                      data-testid="edit-enroll-change-plan-btn"
                    >
                      <ArrowLeftRight className="h-3 w-3 mr-1" /> {t("change_plan")}
                    </Button>
                  </div>
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <Label className="text-xs">{t("starts_on_day")}</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    data-testid="edit-enroll-starts-on-day-input"
                    placeholder="0"
                    value={editEnrollForm.starts_on_day}
                    onChange={(ev) => setEditEnrollForm({ ...editEnrollForm, starts_on_day: ev.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">{t("starts_on_day_hint")}</p>
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="edit-enroll-scholarship" className="text-xs">Beca (no se le cobra)</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Al activarla, el cobro abierto pasa a $0 y queda saldado. Si ya tenía factura, se anula.
                      </p>
                    </div>
                    <Switch
                      id="edit-enroll-scholarship"
                      data-testid="edit-enroll-scholarship-switch"
                      checked={!!editEnrollForm.scholarship}
                      onCheckedChange={(v) => setEditEnrollForm({
                        ...editEnrollForm, scholarship: v,
                        ...(v ? { paid_on_start: false } : {}),
                      })}
                    />
                  </div>
                  {editEnrollForm.scholarship && (
                    <div className="mt-2">
                      <Label htmlFor="edit-enroll-scholarship-cycles" className="text-xs">
                        ¿Cuántos ciclos becados?
                      </Label>
                      <Input
                        id="edit-enroll-scholarship-cycles"
                        type="number"
                        step="1"
                        min="1"
                        data-testid="edit-enroll-scholarship-cycles-input"
                        value={editEnrollForm.scholarship_cycles ?? "1"}
                        onChange={(ev) => setEditEnrollForm({ ...editEnrollForm, scholarship_cycles: ev.target.value })}
                        className="mt-1.5"
                      />
                      <div className="flex items-center justify-between gap-3 mt-2">
                        <Label htmlFor="edit-enroll-scholarship-notify" className="text-xs font-normal">
                          Avisarle por correo que la escuela le cubre las clases
                        </Label>
                        <Switch
                          id="edit-enroll-scholarship-notify"
                          data-testid="edit-enroll-scholarship-notify-switch"
                          checked={editEnrollForm.scholarship_notify !== false}
                          onCheckedChange={(v) => setEditEnrollForm({ ...editEnrollForm, scholarship_notify: v })}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="edit-enroll-paid-on-start" className="text-xs">{t("paid_on_start")}</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">{t("paid_on_start_hint")}</p>
                    </div>
                    <Switch
                      id="edit-enroll-paid-on-start"
                      data-testid="edit-enroll-paid-on-start-switch"
                      checked={editEnrollForm.paid_on_start}
                      disabled={!!editEnrollForm.scholarship}
                      onCheckedChange={(v) => setEditEnrollForm({ ...editEnrollForm, paid_on_start: v })}
                    />
                  </div>
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="edit-enroll-shared-billing" className="text-xs">Facturación compartida (grupo privado)</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">Actívalo cuando dos estudiantes comparten UNA sola factura del programa. Nota: cambiar este ajuste aquí no reajusta las facturas ya emitidas.</p>
                    </div>
                    <Switch
                      id="edit-enroll-shared-billing"
                      data-testid="edit-enroll-shared-billing-switch"
                      checked={editEnrollForm.shared_billing}
                      onCheckedChange={(v) => setEditEnrollForm({ ...editEnrollForm, shared_billing: v })}
                    />
                  </div>
                </div>
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="edit-enroll-flexible-schedule" className="text-xs">{t("flexible_schedule")}</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">{t("flexible_schedule_hint")}</p>
                    </div>
                    <Switch
                      id="edit-enroll-flexible-schedule"
                      data-testid="edit-enroll-flexible-schedule-switch"
                      checked={editEnrollForm.flexible_schedule}
                      onCheckedChange={(v) => setEditEnrollForm({ ...editEnrollForm, flexible_schedule: v })}
                    />
                  </div>
                </div>
                {targetGroup?.is_private && (
                <div className="pt-2 border-t border-amber-200/60">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="edit-enroll-self-booking" className="text-xs">{t("self_booking")}</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">{t("self_booking_hint")}</p>
                    </div>
                    <Switch
                      id="edit-enroll-self-booking"
                      data-testid="edit-enroll-self-booking-switch"
                      checked={editEnrollForm.self_booking}
                      onCheckedChange={(v) => setEditEnrollForm({ ...editEnrollForm, self_booking: v })}
                    />
                  </div>
                </div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpenEditEnroll(false)} data-testid="edit-enroll-cancel-btn">
              {t("cancel")}
            </Button>
            <Button
              onClick={submitEditEnrollment}
              disabled={editEnrollSaving}
              data-testid="edit-enroll-submit-btn"
            >
              {editEnrollSaving ? "…" : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New / Edit Private group dialog (creates group + enrolls 1-2 students in one step) */}
      <Dialog
        open={openPrivate}
        onOpenChange={(o) => {
          setOpenPrivate(o);
          if (o) setPrivStudentQuery("");
          if (!o) setEditingPrivate(null);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-brand" />
              {editingPrivate ? `${t("edit")} · ${privForm.name}` : t("new_private_group")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* LEFT COLUMN: identity, program, pricing, teacher, start date */}
            <div className="space-y-3">
              <div>
                <Label>{t("name")}</Label>
                <Input
                  data-testid="priv-name-input"
                  value={privForm.name}
                  onChange={(e) => setPrivForm({ ...privForm, name: e.target.value })}
                  placeholder="Privado · María López"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("sede")}</Label>
                  <Select value={privForm.sede_id} onValueChange={(v) => setPrivForm({ ...privForm, sede_id: v })}>
                    <SelectTrigger data-testid="priv-sede-select"><SelectValue placeholder={t("select_sede")} /></SelectTrigger>
                    <SelectContent>
                      {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("program")}</Label>
                  <Select value={privForm.program_id} onValueChange={(v) => setPrivForm({
                    ...privForm, program_id: v,
                    self_booking: editingPrivate
                      ? privForm.self_booking
                      : !!programs.find((p) => p.id === v)?.self_booking,
                  })}>
                    <SelectTrigger data-testid="priv-program-select"><SelectValue placeholder={t("select_program")} /></SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t("field_level")}</Label>
                <Select value={privForm.level || "none"} onValueChange={(v) => setPrivForm({ ...privForm, level: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="priv-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("level_none")}</SelectItem>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Pricing card */}
              {privForm.program_id && (() => {
                const selectedProg = programs.find((p) => p.id === privForm.program_id);
                const defaultPrice = selectedProg?.price;
                const defaultCycle = selectedProg?.classes_included;
                return (
                  <div className="p-3 bg-amber-50/60 rounded-md border border-amber-200/60 space-y-2.5" data-testid="priv-pricing-card">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        {t("program_price")}
                      </span>
                      <span className="text-sm font-mono font-semibold text-slate-900" data-testid="priv-program-price">
                        {defaultPrice != null && defaultPrice !== ""
                          ? <>$ {Number(defaultPrice).toFixed(2)} <span className="text-[10px] text-slate-500 font-sans">USD</span></>
                          : <em className="text-slate-400 text-xs not-italic font-sans">{t("program_price_none")}</em>}
                      </span>
                    </div>
                    {!editingPrivate && (
                      <div>
                        <Label className="text-xs">{t("custom_price")}</Label>
                        <div className="relative mt-1.5">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            data-testid="priv-custom-price-input"
                            placeholder={defaultPrice != null ? String(defaultPrice) : "0.00"}
                            value={privForm.custom_price}
                            onChange={(e) => setPrivForm({ ...privForm, custom_price: e.target.value })}
                            className="pl-7 pr-12"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">{t("custom_price_hint")}</p>
                      </div>
                    )}
                    {editingPrivate && (
                      <div>
                        <Label className="text-xs">{t("custom_price")}</Label>
                        <div className="relative mt-1.5">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            data-testid="priv-custom-price-input"
                            placeholder={defaultPrice != null ? String(defaultPrice) : "0.00"}
                            value={privForm.custom_price}
                            onChange={(e) => setPrivForm({ ...privForm, custom_price: e.target.value })}
                            className="pl-7 pr-12"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">{t("custom_price_edit_hint")}</p>
                      </div>
                    )}
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                          {t("program_cycle")}
                        </span>
                        <span className="text-sm font-mono font-semibold text-slate-900" data-testid="priv-program-cycle">
                          {defaultCycle != null && defaultCycle !== ""
                            ? <>{defaultCycle} <span className="text-[10px] text-slate-500 font-sans">{t("sessions_unit")}</span></>
                            : <em className="text-slate-400 text-xs not-italic font-sans">—</em>}
                        </span>
                      </div>
                      <Label className="text-xs">{t("custom_cycle")}</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        data-testid="priv-custom-cycle-input"
                        placeholder={defaultCycle != null ? String(defaultCycle) : "0"}
                        value={privForm.custom_cycle}
                        onChange={(e) => setPrivForm({ ...privForm, custom_cycle: e.target.value })}
                        className="mt-1.5"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">{t("custom_cycle_hint")}</p>
                    </div>
                    <div className="pt-2 border-t border-amber-200/60">
                      <Label className="text-xs">{t("starts_on_day")}</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        data-testid="priv-starts-on-day-input"
                        placeholder="0"
                        value={privForm.starts_on_day}
                        onChange={(e) => setPrivForm({ ...privForm, starts_on_day: e.target.value })}
                        className="mt-1.5"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">{t("starts_on_day_hint")}</p>
                    </div>
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <Label htmlFor="priv-scholarship" className="text-xs">Beca (no se le cobra)</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            El programa mantiene su precio, pero el cobro sale en $0 y ya saldado. Sin factura ni correo de pago.
                          </p>
                        </div>
                        <Switch
                          id="priv-scholarship"
                          data-testid="priv-scholarship-switch"
                          checked={!!privForm.scholarship}
                          onCheckedChange={(v) => setPrivForm({
                            ...privForm, scholarship: v,
                            ...(v ? { paid_on_start: false, exclude_from_revenue: false } : {}),
                          })}
                        />
                      </div>
                      {privForm.scholarship && (
                        <div className="mt-2">
                          <Label htmlFor="priv-scholarship-cycles" className="text-xs">
                            ¿Cuántos ciclos becados?
                          </Label>
                          <Input
                            id="priv-scholarship-cycles"
                            type="number"
                            step="1"
                            min="1"
                            data-testid="priv-scholarship-cycles-input"
                            value={privForm.scholarship_cycles ?? "1"}
                            onChange={(e) => setPrivForm({ ...privForm, scholarship_cycles: e.target.value })}
                            className="mt-1.5"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">
                            Al terminar esos ciclos, la renovación vuelve a salir al precio normal.
                          </p>
                          <div className="flex items-center justify-between gap-3 mt-2">
                            <Label htmlFor="priv-scholarship-notify" className="text-xs font-normal">
                              Avisarle por correo que la escuela le cubre las clases
                            </Label>
                            <Switch
                              id="priv-scholarship-notify"
                              data-testid="priv-scholarship-notify-switch"
                              checked={privForm.scholarship_notify !== false}
                              onCheckedChange={(v) => setPrivForm({ ...privForm, scholarship_notify: v })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <Label htmlFor="priv-paid-on-start" className="text-xs">{t("paid_on_start")}</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">{t("paid_on_start_hint")}</p>
                        </div>
                        <Switch
                          id="priv-paid-on-start"
                          data-testid="priv-paid-on-start-switch"
                          checked={!!privForm.paid_on_start}
                          disabled={!!privForm.scholarship}
                          onCheckedChange={(v) => setPrivForm({ ...privForm, paid_on_start: v, exclude_from_revenue: v ? privForm.exclude_from_revenue : false })}
                        />
                      </div>
                    </div>
                    {privForm.paid_on_start && (
                      <div className="pt-2 border-t border-amber-200/60">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <Label htmlFor="priv-historical" className="text-xs">{t("historical_payment")}</Label>
                            <p className="text-[11px] text-slate-500 mt-0.5">{t("historical_payment_hint")}</p>
                          </div>
                          <Switch
                            id="priv-historical"
                            data-testid="priv-historical-switch"
                            checked={!!privForm.exclude_from_revenue}
                            onCheckedChange={(v) => setPrivForm({ ...privForm, exclude_from_revenue: v })}
                          />
                        </div>
                      </div>
                    )}
                    <div className="pt-2 border-t border-amber-200/60 grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Fecha de venta</Label>
                        <Input
                          type="date"
                          data-testid="priv-sale-date-input"
                          value={privForm.sale_date || privForm.start_date || ""}
                          onChange={(e) => setPrivForm({ ...privForm, sale_date: e.target.value })}
                          className="mt-1.5"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">En qué mes cuenta la venta</p>
                      </div>
                      {privForm.paid_on_start && (
                        <div>
                          <Label className="text-xs">Fecha de pago</Label>
                          <Input
                            type="date"
                            data-testid="priv-paid-date-input"
                            value={privForm.paid_date || today}
                            onChange={(e) => setPrivForm({ ...privForm, paid_date: e.target.value })}
                            className="mt-1.5"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">En qué mes entra la plata</p>
                        </div>
                      )}
                    </div>

                    {privForm.student_ids.length === 2 && !editingPrivate && (
                      <div className="pt-2 border-t border-amber-200/60">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <Label htmlFor="priv-shared-billing" className="text-xs">Facturación compartida (una sola factura para la pareja)</Label>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Actívalo cuando el precio es de la PAREJA. Se emite una sola factura,
                              con un solo consecutivo, y el segundo estudiante queda listado en ella
                              (la ve y la puede pagar). Apagado = una factura por cada estudiante.
                            </p>
                          </div>
                          <Switch
                            id="priv-shared-billing"
                            data-testid="priv-shared-billing-switch"
                            checked={!!privForm.shared_billing}
                            onCheckedChange={(v) => setPrivForm({ ...privForm, shared_billing: v })}
                          />
                        </div>
                      </div>
                    )}
                    {!privForm.paid_on_start && (
                      <div className="pt-2 border-t border-amber-200/60">
                        <Label className="text-xs">{t("payment_link")}</Label>
                        <Input
                          type="url"
                          placeholder="https://..."
                          value={privForm.payment_link || ""}
                          onChange={(e) => setPrivForm({ ...privForm, payment_link: e.target.value })}
                          className="mt-1.5"
                          data-testid="priv-payment-link-input"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">{t("priv_payment_link_hint")}</p>
                      </div>
                    )}
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <Label htmlFor="priv-send-welcome" className="text-xs">Enviar correo de bienvenida</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {privForm.scholarship
                              ? "Con beca no se emite factura: el correo solo le da la bienvenida y su acceso al panel."
                              : "Apagado: la factura del ciclo 1 se emite igual, solo no se envía el correo."}
                          </p>
                        </div>
                        <Switch
                          id="priv-send-welcome"
                          data-testid="priv-send-welcome-switch"
                          checked={privForm.send_welcome_email !== false}
                          onCheckedChange={(v) => setPrivForm({ ...privForm, send_welcome_email: v })}
                        />
                      </div>
                    </div>
                    <div className="pt-2 border-t border-amber-200/60">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <Label htmlFor="priv-self-booking" className="text-xs">{t("self_booking")}</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">{t("self_booking_hint")}</p>
                        </div>
                        <Switch
                          id="priv-self-booking"
                          data-testid="priv-self-booking-switch"
                          checked={!!privForm.self_booking}
                          onCheckedChange={(v) => setPrivForm({ ...privForm, self_booking: v })}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div>
                <Label>{t("teacher")}</Label>
                <Select value={privForm.teacher_id || "none"} onValueChange={(v) => setPrivForm({ ...privForm, teacher_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="priv-teacher-select"><SelectValue placeholder={t("select_teacher")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {teachers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("field_meet_platform")}</Label>
                <Select value={privForm.meet_platform || "__default__"} onValueChange={(v) => setPrivForm({ ...privForm, meet_platform: v === "__default__" ? "" : v })}>
                  <SelectTrigger data-testid="priv-meet-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">{t("platform_default")}</SelectItem>
                    <SelectItem value="meet">{t("platform_meet")}</SelectItem>
                    <SelectItem value="zoom">{t("platform_zoom")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-brand-blue" /> {t("start_date")}
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    type="date"
                    data-testid="priv-start-date-input"
                    value={privForm.start_date || ""}
                    onChange={(e) => setPrivForm({ ...privForm, start_date: e.target.value })}
                    className="flex-1"
                  />
                  {privForm.start_date && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPrivForm({ ...privForm, start_date: "" })}
                      data-testid="priv-clear-start-date"
                    >
                      Sin definir
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{t("start_date_hint")}</p>
                {!privForm.start_date && (
                  <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                    🕒 El correo dirá que el horario está pendiente de asignación.
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: schedule + searchable student picker */}
            <div className="space-y-3">
              <div>
                <Label className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-brand-blue" /> {t("schedule")}
                </Label>
                <div className="mt-2">
                  <ScheduleEditor
                    testIdPrefix="priv-schedule"
                    days={privForm.schedule_days}
                    startTime={privForm.schedule_start_time}
                    endTime={privForm.schedule_end_time}
                    timesByDay={privForm.schedule_times_by_day}
                    onChange={({ days, startTime, endTime, timesByDay }) =>
                      setPrivForm({ ...privForm, schedule_days: days, schedule_start_time: startTime, schedule_end_time: endTime, schedule_times_by_day: timesByDay })
                    }
                  />
                </div>
              </div>

              {/* Searchable student picker (creation only; existing private students aren't re-picked from here) */}
              {!editingPrivate && (
                <div>
                <Label>{t("add_students")}</Label>
                <p className="text-[11px] text-slate-500 mt-1">{t("select_up_to_2")}</p>

                {/* Selected chips */}
                {privForm.student_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="priv-selected-chips">
                    {privForm.student_ids.map((sid) => {
                      const s = students.find((x) => x.user_id === sid);
                      if (!s) return null;
                      return (
                        <span
                          key={sid}
                          data-testid={`priv-chip-${sid}`}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-brand-blue/10 text-brand-blue rounded-full text-xs font-medium border border-brand-blue/30"
                        >
                          <UserCheck className="h-3 w-3" />
                          {s.name}
                          <button
                            type="button"
                            onClick={() =>
                              setPrivForm({
                                ...privForm,
                                student_ids: privForm.student_ids.filter((x) => x !== sid),
                              })
                            }
                            className="ml-0.5 text-brand-blue/60 hover:text-brand-blue"
                            aria-label={t("remove")}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Search input */}
                <div className="relative mt-2">
                  <Input
                    type="search"
                    placeholder={t("student_search_placeholder")}
                    value={privStudentQuery}
                    onChange={(e) => setPrivStudentQuery(e.target.value)}
                    data-testid="priv-student-search"
                    disabled={privForm.student_ids.length >= 2}
                    className="pr-8"
                  />
                </div>

                {/* Filtered results — capped to 8, only show when there's a query OR < 5 students */}
                {(() => {
                  const q = privStudentQuery.trim().toLowerCase();
                  const matches = students.filter((s) => {
                    if (privForm.student_ids.includes(s.user_id)) return false;
                    if (!q) return true;
                    return (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
                  }).slice(0, 8);
                  const atCapacity = privForm.student_ids.length >= 2;
                  if (atCapacity) {
                    return (
                      <p className="text-[11px] text-amber-700 mt-2 italic">{t("student_picker_full")}</p>
                    );
                  }
                  return (
                    <div className="mt-1.5 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white" data-testid="priv-student-results">
                      {matches.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-4">
                          {q ? t("student_picker_no_match") : t("no_data")}
                        </p>
                      ) : (
                        matches.map((s) => (
                          <button
                            key={s.user_id}
                            type="button"
                            data-testid={`priv-student-${s.user_id}`}
                            onClick={() =>
                              setPrivForm({
                                ...privForm,
                                student_ids: [...privForm.student_ids, s.user_id],
                              })
                            }
                            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors"
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 truncate">{s.name}</div>
                              <div className="text-xs text-slate-500 truncate">{s.email}</div>
                            </div>
                            <Plus className="h-4 w-4 text-brand-blue shrink-0" />
                          </button>
                        ))
                      )}
                    </div>
                  );
                })()}

                <p className="text-[11px] text-slate-500 mt-2">
                  {privForm.student_ids.length === 0
                    ? "—"
                    : `${privForm.student_ids.length === 1 ? t("private_individual") : t("private_pair")} · ${t("capacity")}: ${privForm.student_ids.length}/${privForm.student_ids.length === 1 ? 1 : 2}`}
                </p>
                </div>
              )}

              {/* Read-only enrolled students banner in edit mode */}
              {editingPrivate && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md" data-testid="priv-enrolled-readonly">
                  <Label className="text-xs">{t("students")}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {privForm.student_ids.map((sid) => {
                      const s = students.find((x) => x.user_id === sid);
                      if (!s) return null;
                      return (
                        <span
                          key={sid}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-brand-blue/10 text-brand-blue rounded-full text-xs font-medium border border-brand-blue/30"
                        >
                          <UserCheck className="h-3 w-3" />
                          {s.name}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">{t("edit_private_students_hint")}</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {editingPrivate && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mr-auto text-xs"
                data-testid="priv-convert-to-group-btn"
                onClick={() => {
                  const g = groups.find((x) => x.id === editingPrivate.groupId);
                  if (g) convertPrivateToGroup(g);
                }}
                title="Cambiar este grupo privado a grupo público"
              >
                <UsersIcon className="h-3.5 w-3.5 mr-1.5" />
                Convertir a grupo público
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpenPrivate(false)}>{t("cancel")}</Button>
            <Button
              type="button"
              variant="outline"
              data-testid="priv-preview-email-btn"
              disabled={!privForm.program_id || privForm.student_ids.length === 0}
              onClick={() => {
                const firstStudent = students.find((s) => s.user_id === privForm.student_ids[0]);
                // For "create" mode there is no real group_id yet; we still want a sensible preview
                // by using the first program/sede so the helper can build the card.
                const draftLine = {
                  group_id: editingPrivate?.groupId || "preview",
                  start_date: privForm.start_date || null,
                  payment_link: (privForm.payment_link || "").trim() || null,
                  bank_account: null,
                  amount: privForm.custom_price !== "" ? Number(privForm.custom_price) : null,
                  classes_included: privForm.custom_cycle !== "" ? Number(privForm.custom_cycle) : null,
                  starts_on_day: privForm.starts_on_day !== "" ? Number(privForm.starts_on_day) : null,
                  paid_on_start: !!privForm.paid_on_start,
                  // Inline overrides so the preview matches the actual email
                  // even before the group exists in the DB (create mode).
                  group_name: privForm.name || null,
                  sede_id: privForm.sede_id || null,
                  program_id: privForm.program_id || null,
                  teacher_id: privForm.teacher_id || null,
                  schedule_days: privForm.schedule_days || [],
                  schedule_start_time: privForm.schedule_start_time || null,
                  schedule_end_time: privForm.schedule_end_time || null,
                  schedule_times_by_day: privForm.schedule_times_by_day || null,
                  meet_platform: privForm.meet_platform || null,
                  is_private: true,
                };
                openWelcomePreview({
                  student_id: firstStudent?.user_id,
                  student_name: firstStudent?.name,
                  draft: [draftLine],
                });
              }}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              {t("preview_welcome_email")}
            </Button>
            <Button
              data-testid="save-private-group-button"
              onClick={submitPrivate}
              disabled={!privForm.name || !privForm.sede_id || !privForm.program_id || privForm.student_ids.length === 0}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome email preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("preview_welcome_email")}</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Mail className="h-6 w-6 text-blue-600 animate-pulse" />
            </div>
          ) : previewError ? (
            <div className="py-10 text-center text-sm text-rose-700" data-testid="preview-error">{previewError}</div>
          ) : previewHtml ? (
            <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50">
              <iframe
                title="email-preview"
                data-testid="preview-iframe"
                srcDoc={previewHtml}
                className="w-full"
                style={{ height: "60vh", border: 0 }}
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="preview-close-btn">
              {t("close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
